import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    linkedSignal,
    output,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Workflow } from "../service/Workflow";
import { S25Util } from "../../../util/s25-util";
import { WorkflowJsonPayloadBuilderModalComponent } from "../workflow.json.payload.builder.modal.component";
import { IngestionFieldDefinition } from "../../data-ingestion/data.ingestion.service";
import { WorkflowUtil } from "../workflow.util";

interface HttpHeader {
    name: string;
    value: string;
}

@Component({
    standalone: true,
    selector: "s25-ng-workflow-http-action",
    template: `
        <div class="form-group">
            <label>HTTP Method</label>
            <select [(ngModel)]="action().httpMethod" class="form-control">
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
            </select>
        </div>

        <div class="form-group">
            <label>URL *</label>
            <input
                type="url"
                [(ngModel)]="action().httpUrl"
                class="form-control"
                placeholder="https://api.example.com/webhook"
                required
            />
            @for (error of urlErrors(); track $index) {
                <div [class]="error.startsWith('Field reference') ? 'validation-warning' : 'validation-error'">
                    <i class="fa fa-exclamation-circle"></i> {{ error }}
                </div>
            }
        </div>

        <div class="form-group">
            <label>HTTP Headers</label>
            <div class="headers-section">
                @for (header of headers(); track header) {
                    <div class="header-pair">
                        <div class="header-inputs">
                            <label>Header Name</label>
                            <input
                                type="text"
                                [(ngModel)]="header.name"
                                (ngModelChange)="onHeaderChange()"
                                class="form-control header-name"
                                placeholder="Content-Type"
                            />
                        </div>
                        <div class="header-inputs">
                            <label>Header Value</label>
                            <input
                                type="text"
                                [(ngModel)]="header.value"
                                (ngModelChange)="onHeaderChange()"
                                class="form-control header-value"
                                placeholder="application/json"
                            />
                        </div>
                        <button
                            type="button"
                            class="aw-button aw-button--danger--outline remove-header-btn"
                            (click)="removeHeader($index)"
                            title="Remove header"
                        >
                            ×
                        </button>
                    </div>
                }
                <button type="button" class="aw-button aw-button--outline add-header-btn" (click)="addHeader()">
                    Add Header
                </button>
            </div>
        </div>

        <div class="form-group">
            <label>JSON Payload Template</label>
            <div class="payload-controls">
                <button
                    class="aw-button aw-button--outline payload-builder-toggle"
                    (click)="openPayloadBuilderModal()"
                    type="button"
                >
                    Open Payload Builder
                </button>
            </div>

            <textarea
                [(ngModel)]="action().jsonPayloadTemplate"
                (ngModelChange)="changed.emit()"
                class="form-control"
                rows="6"
                placeholder='Use field variables like $CNET-123-FIELD_CODE or functions like $CNET-fn-split("value", ",")'
            ></textarea>
            @for (error of payloadErrors(); track $index) {
                <div [class]="error.startsWith('Field reference') ? 'validation-warning' : 'validation-error'">
                    <i class="fa fa-exclamation-circle"></i> {{ error }}
                </div>
            }
        </div>

        <!-- JSON Payload Builder Modal -->
        <workflow-json-payload-builder-modal
            [availableFields]="formFields()"
            [initialPayload]="action().jsonPayloadTemplate || ''"
            (payloadChange)="onPayloadChange($event)"
            (payloadSave)="onPayloadChange($event)"
        ></workflow-json-payload-builder-modal>
    `,
    styles: `
        .form-group {
            margin-bottom: 16px;
        }

        .form-group label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
            color: #555;
        }

        .form-control {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            box-sizing: border-box;
        }

        .form-control:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        select.form-control {
            background-color: white;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
            background-position: right 8px center;
            background-repeat: no-repeat;
            background-size: 16px 16px;
            padding-right: 40px;
        }

        .headers-section {
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 12px;
            background: #f9f9f9;
        }

        .header-pair {
            display: flex;
            align-items: end;
            gap: 12px;
            margin-bottom: 12px;
        }

        .header-inputs {
            flex: 1;
        }

        .header-inputs label {
            display: block;
            margin-bottom: 4px;
            font-size: 12px;
            font-weight: 500;
            color: #666;
        }

        .header-name,
        .header-value {
            width: 100%;
        }

        .remove-header-btn {
            flex-shrink: 0;
            width: 32px;
            height: 32px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            font-weight: bold;
        }

        .add-header-btn {
            font-size: 12px;
            padding: 6px 12px;
        }

        .payload-controls {
            margin-bottom: 8px;
        }

        .payload-builder-toggle {
            font-size: 12px;
            padding: 6px 12px;
        }

        .validation-error {
            color: var(--color-error-text, #dc3545);
            font-size: 12px;
            margin-top: 4px;
            padding: 4px 8px;
            background: var(--color-error-bg, #f8d7da);
            border: 1px solid var(--color-error-border, #f5c6cb);
            border-radius: 4px;
        }

        .validation-warning {
            color: var(--color-warning-text, #856404);
            font-size: 12px;
            margin-top: 4px;
            padding: 4px 8px;
            background: var(--color-warning-bg, #fff3cd);
            border: 1px solid var(--color-warning-border, #ffeaa7);
            border-radius: 4px;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, FormsModule, WorkflowJsonPayloadBuilderModalComponent],
})
export class WorkflowHttpActionComponent {
    action = input.required<Workflow.WorkflowAction>();
    formFields = input.required<IngestionFieldDefinition[]>();

    changed = output<void>();

    readonly urlErrors = computed(() =>
        WorkflowUtil.validateFieldReferences(this.action().httpUrl ?? "", this.formFields(), undefined),
    );
    readonly payloadErrors = computed(() =>
        WorkflowUtil.validateFieldReferences(this.action().jsonPayloadTemplate ?? "", this.formFields(), undefined),
    );

    payloadBuilderModal = viewChild(WorkflowJsonPayloadBuilderModalComponent);

    headers = linkedSignal(() => {
        const key = this.getActionKey(this.action());

        let headers: HttpHeader[] = [];
        if (this.action().httpHeaders) {
            const [parsed, error] = S25Util.parseJson(this.action().httpHeaders);
            if (error) {
                console.warn("Failed to parse headers for action", key, error);
            }
            if (parsed) {
                headers = Object.entries(parsed).map(([name, value]) => ({ name, value: String(value) }));
            }
        }

        // Ensure at least one header (Content-Type) exists for new actions
        if (headers.length === 0) {
            headers.push({ name: "Content-Type", value: "application/json" });
        }

        return headers;
    });

    addHeader() {
        this.headers.update((headers) => [...headers, { name: "", value: "" }]);
        this.updateActionHttpHeaders();
        this.changed.emit();
    }

    removeHeader(index: number) {
        this.headers.update((headers) => S25Util.array.toSpliced(headers, index, 1));
        this.updateActionHttpHeaders();
        this.changed.emit();
    }

    onHeaderChange() {
        this.updateActionHttpHeaders();
        this.changed.emit();
    }

    updateActionHttpHeaders() {
        // Convert headers array to JSON object
        const obj = Object.fromEntries(
            this.headers()
                .filter((h) => h.name && h.value)
                .map((h) => [h.name, h.value]),
        );
        this.action().httpHeaders = JSON.stringify(obj);
    }

    openPayloadBuilderModal() {
        this.payloadBuilderModal()?.openModal();
    }

    onPayloadChange(payload: string) {
        this.action().jsonPayloadTemplate = payload;
        this.changed.emit();
    }

    private getActionKey(action: Workflow.WorkflowAction): string {
        return (action.id || action.workflowActionId || "new").toString();
    }
}
